import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Fade, Stack } from '@mui/material'
import { useSelector } from 'react-redux'
import LoadingBackdrop from '../../shared/components/LoadingBackdrop/LoadingBackdrop'
import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder'
import Tabs from '../../shared/components/Tabs/Tabs'
import useTppDisplayName from '../../shared/hooks/useDisplayNames/variants/useTppDisplayName'
import { useHistory, useLocation } from 'react-router-dom'
import TPPCellLinkModalProvider from '../../views/TPP/components/TPPCell/TPPCellLinkModalProvider'
import { TPPChangeLogModalProvider, useTPPChangeLogModalContext } from '../TPPChangeLog'
import { useGetTPPColumnVersionQuery } from '../TPPChangeLog/queries/useGetTPPColumnVersionQuery'
import { RootState } from '../../redux/types'

import { useGetTppIdQuery } from './hooks/useGetTppIdQuery'
import { useTppData } from './hooks/useTppData'
import { useTppMutations } from './hooks/useTppMutations'
import { useTppDnD } from './hooks/useTppDnD'
import { useTppViewModel } from './hooks/useTppViewModel'
import { useResizeObserverFix } from './hooks/useResizeObserverFix'
import { useTppPermissions } from './hooks/useTppPermissions'
import TppHeader from './components/TppHeader'
import { TppSectionRowFilter } from './components/TppSectionRowFilter'
import TppGrid from './components/TppGrid'
import SaveVersionModal from './components/SaveVersionModal'
import kebabCase from 'lodash/kebabCase'
import { TppCell } from './types'
import { getAcronym } from '../../shared/utils/acronym'

type SavedVersionInfo = {
    columnTitle: string
    savedAt: Date
} | null

const TppViewContent = () => {
    const strategyId = useSelector((state: RootState) => state.strategy?._id)
    const { data: tppId, isLoading: tppIdLoading, isFetched: tppIdFetched } = useGetTppIdQuery(strategyId)

    const [viewMode, setViewMode] = useState<'all' | 'summary'>('all')
    const handleViewModeChange = (mode: 'all' | 'summary') => setViewMode(mode)
    const [creatingCellKey, setCreatingCellKey] = useState<string | null>(null)
    const [movingCellId, setMovingCellId] = useState<string | null>(null)
    const [movingSlotId, setMovingSlotId] = useState<string | null>(null)
    const [savedVersionInfo, setSavedVersionInfo] = useState<SavedVersionInfo>(null)
    const [saveVersionModalColumnId, setSaveVersionModalColumnId] = useState<string | null>(null)

    const isSummary = viewMode === 'summary'

    const { canEdit } = useTppPermissions()

    const { matrixState, isLoading } = useTppData(tppId)
    const mutations = useTppMutations(tppId)
    const { sensors, handleDragStart, handleDragEnd } = useTppDnD(
        matrixState,
        mutations,
        {
            onCellMoveStart: (cellId) => setMovingCellId(cellId),
            onCellMoveEnd: () => setMovingCellId(null),
            onSlotReorderStart: (slotId) => setMovingSlotId(slotId),
            onSlotReorderEnd: () => setMovingSlotId(null),
        },
        { disabled: !canEdit }
    )
    const tppFilters = useSelector((state: RootState) => state.tppFilters)
    const {
        handleOpen: openChangeLogModal,
        comparisonState,
        handleStopCompare,
        handleSetComparisonData,
    } = useTPPChangeLogModalContext()

    // Fetch comparison version data when comparison is active
    const { data: comparisonVersionData } = useGetTPPColumnVersionQuery(comparisonState?.versionId ?? null)

    // Update comparison data in context when fetched
    useEffect(() => {
        if (comparisonVersionData) {
            handleSetComparisonData(comparisonVersionData)
        }
    }, [comparisonVersionData, handleSetComparisonData])

    useResizeObserverFix()

    const location = useLocation()
    const history = useHistory()
    const tppDisplayName = useTppDisplayName()
    const selectedTabId = location.pathname.split('/')[2]
    const isArchivedTab = selectedTabId === 'archived'

    const hiddenColumnIds = tppFilters?.hiddenColumnIds || []
    const hiddenRowIds = tppFilters?.hiddenRowIds || []
    const hiddenSectionIds = tppFilters?.hiddenSectionIds || []
    const showContext = !!tppFilters?.showContext

    const {
        cellLookupForView,
        visibleColumnIds,
        filteredSectionIdsWithRows,
        sectionRowIds,
        rowMapForView,
        pinnedColumnIdsForView,
        pinnedColumnIndexById,
        tabsData,
    } = useTppViewModel(matrixState, viewMode, selectedTabId, {
        hiddenColumnIds,
        hiddenRowIds,
        hiddenSectionIds,
    })

    // From HEAD - Change Log modal handler
    const handleOpenChangeLog = useCallback(() => {
        if (!matrixState) return

        const columns = Object.values(matrixState.columns).map((col) => ({
            _id: col._id,
            title: col.title,
        }))

        openChangeLogModal({
            tppId,
            columns,
        })
    }, [matrixState, openChangeLogModal, tppId])

    // Open save version modal
    const handleSaveVersionClick = useCallback((columnId: string) => {
        setSaveVersionModalColumnId(columnId)
    }, [])

    // Save version handler (called from modal)
    const handleSaveVersion = useCallback(
        async (note: string) => {
            if (!saveVersionModalColumnId) return
            const column = matrixState?.columns[saveVersionModalColumnId]
            if (!column) return

            await mutations.createColumnVersion.mutateAsync({
                columnId: saveVersionModalColumnId,
                note: note || undefined,
            })
            setSavedVersionInfo({
                columnTitle: column.title,
                savedAt: new Date(),
            })
            setSaveVersionModalColumnId(null)
        },
        [matrixState, mutations.createColumnVersion, saveVersionModalColumnId]
    )

    // From feature/tpp
    const visibleColumnOptions = useMemo(
        () => visibleColumnIds.map((id) => ({ id, label: matrixState?.columns[id]?.title || id })),
        [matrixState?.columns, visibleColumnIds]
    )
    const cellsById = useMemo(() => {
        const map: Record<string, TppCell> = {}
        if (!matrixState?.cellLookup) return map
        Object.values(matrixState.cellLookup).forEach((columns) => {
            Object.values(columns).forEach((cell) => {
                map[cell._id] = cell
            })
        })
        return map
    }, [matrixState?.cellLookup])

    const tabs = useMemo(
        () =>
            tabsData.map((tabLabel) => ({
                id: kebabCase(tabLabel),
                label: tabLabel,
                view: (
                    <TppGrid
                        columnIds={visibleColumnIds}
                        pinnedColumnIndexById={pinnedColumnIndexById}
                        pinnedColumnIds={pinnedColumnIdsForView}
                        sectionIds={filteredSectionIdsWithRows}
                        sectionRowIds={sectionRowIds}
                        columns={matrixState!.columns}
                        sections={matrixState!.sections}
                        rows={rowMapForView}
                        slots={matrixState!.slots}
                        cellLookup={cellLookupForView}
                        columnsById={matrixState!.columns}
                        cellsById={cellsById}
                        sensors={sensors}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onPinToggle={async (columnId) => {
                            await mutations.togglePinColumn.mutateAsync(columnId)
                        }}
                        onLockToggle={async (columnId) => {
                            await mutations.toggleLockColumn.mutateAsync(columnId)
                        }}
                        onArchiveToggle={async (columnId) => {
                            await mutations.toggleArchiveColumn.mutateAsync(columnId)
                        }}
                        onSaveVersionClick={handleSaveVersionClick}
                        isPinning={mutations.togglePinColumn.isLoading}
                        isLocking={mutations.toggleLockColumn.isLoading}
                        isArchiving={mutations.toggleArchiveColumn.isLoading}
                        isArchivedTab={isArchivedTab}
                        onAddSlot={(rowId) => mutations.createSlot.mutate({ rowId })}
                        isAddingSlot={mutations.createSlot.isLoading}
                        isSummary={isSummary}
                        visibleColumns={visibleColumnOptions}
                        creatingCellKey={creatingCellKey}
                        onCreateCell={async (slotId, columnId) => {
                            const key = `${slotId}:${columnId}`
                            setCreatingCellKey(key)
                            try {
                                await mutations.createCell.mutateAsync({ slotId, columnId })
                            } finally {
                                setCreatingCellKey(null)
                            }
                        }}
                        movingCellId={movingCellId}
                        movingSlotId={movingSlotId}
                        showContext={showContext}
                        onUpdateSlotContext={async (slotId, context) => {
                            await mutations.updateSlotContext.mutateAsync({ slotId, context })
                        }}
                        cornerHeaderContent={
                            <TppSectionRowFilter
                                sections={matrixState.sections}
                                rows={matrixState.rows}
                                sectionIds={matrixState.tpp.sectionIds}
                            />
                        }
                        comparisonState={comparisonState}
                        comparisonData={comparisonVersionData}
                        onCloseComparison={handleStopCompare}
                        canEdit={canEdit}
                    />
                ),
            })),
        [
            tabsData,
            sensors,
            handleDragStart,
            handleDragEnd,
            visibleColumnIds,
            matrixState,
            cellsById,
            mutations.togglePinColumn,
            mutations.toggleLockColumn,
            mutations.toggleArchiveColumn,
            mutations.createColumnVersion,
            mutations.createSlot,
            mutations.updateSlotContext,
            mutations.createCell,
            handleSaveVersionClick,
            isArchivedTab,
            visibleColumnOptions,
            creatingCellKey,
            movingCellId,
            movingSlotId,
            showContext,
            filteredSectionIdsWithRows,
            pinnedColumnIdsForView,
            pinnedColumnIndexById,
            sectionRowIds,
            rowMapForView,
            cellLookupForView,
            comparisonState,
            comparisonVersionData,
            handleStopCompare,
        ]
    )

    const handleTabChange = (tabId: string) => {
        const path = `/tpp/${tabId}`
        if (selectedTabId) {
            history.push(path)
        } else {
            history.replace(path)
        }
    }

    const tppAcronymDisplayName = getAcronym(tppDisplayName.singular)

    const noTppForRoadmap = !!strategyId && tppIdFetched && !tppIdLoading && tppId == null
    if (noTppForRoadmap)
        return (
            <Fade in timeout={800}>
                <EmptyPlaceholder
                    title={tppDisplayName.singular}
                    subtitle={`${tppAcronymDisplayName} is not available for this roadmap`}
                />
            </Fade>
        )

    if (tppIdLoading || isLoading || !matrixState)
        return (
            <LoadingBackdrop title={tppAcronymDisplayName} subtitle={`Fetching ${tppDisplayName.singular}`} loading />
        )

    return (
        <Fade in timeout={800}>
            <Stack flex={1} direction="column">
                <TppHeader
                    tppId={tppId}
                    title={tppDisplayName.singular}
                    assetAmbition={matrixState.tpp?.assetAmbition}
                    viewMode={viewMode}
                    onViewModeChange={handleViewModeChange}
                    onOpenChangeLog={handleOpenChangeLog}
                    savedVersionInfo={savedVersionInfo}
                    onSavedVersionDismiss={() => setSavedVersionInfo(null)}
                    sections={(matrixState.tpp.sectionIds || []).map((id) => matrixState!.sections[id]).filter(Boolean)}
                    rows={Object.values(matrixState.rows)}
                    canEdit={canEdit}
                />

                <Tabs
                    orientation="horizontal"
                    tabs={tabs}
                    selectedTabId={selectedTabId}
                    onTabChange={handleTabChange}
                    tabPanelSx={{ px: 0, py: 3 }}
                />

                <SaveVersionModal
                    open={!!saveVersionModalColumnId}
                    onClose={() => setSaveVersionModalColumnId(null)}
                    onSave={handleSaveVersion}
                    isLoading={mutations.createColumnVersion.isLoading}
                    columnTitle={
                        saveVersionModalColumnId ? matrixState.columns[saveVersionModalColumnId]?.title || '' : ''
                    }
                />
            </Stack>
        </Fade>
    )
}

const TppView = () => {
    return (
        <TPPChangeLogModalProvider>
            <TPPCellLinkModalProvider>
                <TppViewContent />
            </TPPCellLinkModalProvider>
        </TPPChangeLogModalProvider>
    )
}

export default TppView
